import { UploadCloud } from 'lucide-react';
import { type DragEvent, type ReactNode, useId, useRef, useState } from 'react';

import { cx } from '@/lib/cx';
import { toast } from '@/ui/primitives/aviso';

interface DropzoneProps {
  /** Extensões aceitas, ex.: ['.xml', '.zip']. Ausente = qualquer arquivo (quem valida é o servidor). */
  aceita?: readonly string[];
  multiplo?: boolean;
  aoSoltar: (arquivos: File[]) => void;
  /**
   * Arquivos fora de `aceita`. Sem este callback, um aviso diz quais foram ignorados — arquivo
   * recusado nunca some em silêncio (produção, 14/10/2026).
   */
  aoRecusar?: (arquivos: File[]) => void;
  titulo: string;
  descricao: ReactNode;
  disabled?: boolean;
  /** Faixa baixa numa linha só — quando já há arquivos escolhidos e a área só "acrescenta". */
  compacto?: boolean;
}

const aceito = (arquivo: File, aceita: readonly string[] | undefined) =>
  !aceita || aceita.some((ext) => arquivo.name.toLowerCase().endsWith(ext));

function avisarRecusa(arquivos: File[], aceita: readonly string[] | undefined) {
  const nomes = arquivos.map((a) => a.name).join(', ');
  toast.error(`Arquivo ignorado: ${nomes}`, {
    description: `Aceitos aqui: ${(aceita ?? []).join(', ')}.`,
  });
}

function Rotulo(p: {
  compacto: boolean;
  arrastando: number;
  titulo: string;
  descricao: ReactNode;
  idDescricao: string;
}) {
  const arrastandoAgora = p.arrastando > 0;
  return (
    <>
      <span
        aria-hidden
        className={cx(
          'grid shrink-0 place-items-center rounded-cartao bg-marca-suave text-marca transition-transform duration-[var(--duracao-media)]',
          p.compacto ? 'size-9' : 'size-12',
          arrastandoAgora && '-translate-y-1',
        )}
      >
        <UploadCloud className={p.compacto ? 'size-5' : 'size-6'} />
      </span>
      <span className="grid gap-0.5">
        <span className={cx('font-semibold', p.compacto ? 'text-base' : 'text-lg')}>
          {arrastandoAgora
            ? `Solte ${p.arrastando === 1 ? 'o arquivo' : `os ${String(p.arrastando)} arquivos`}`
            : p.titulo}
        </span>
        {/* Fora do nome do botão (aria-hidden), mas lida como descrição (aria-describedby). */}
        <span id={p.idDescricao} aria-hidden className="text-sm font-normal text-texto-suave">
          {p.descricao}
        </span>
      </span>
    </>
  );
}

/**
 * Área de soltar arquivos que reage ao arraste (a borda "respira" e a contagem aparece) e
 * também funciona por teclado/clique (é um botão que abre o seletor).
 */
export function Dropzone({
  aceita,
  multiplo = true,
  aoSoltar,
  aoRecusar,
  titulo,
  descricao,
  disabled = false,
  compacto = false,
}: DropzoneProps) {
  const id = useId();
  const entrada = useRef<HTMLInputElement>(null);
  const [arrastando, setArrastando] = useState(0);

  const receber = (lista: FileList | null) => {
    const todos = Array.from(lista ?? []);
    const aceitos = todos.filter((a) => aceito(a, aceita));
    const recusados = todos.filter((a) => !aceito(a, aceita));
    if (aceitos.length > 0) aoSoltar(aceitos);
    if (recusados.length === 0) return;
    if (aoRecusar) aoRecusar(recusados);
    else avisarRecusa(recusados, aceita);
  };

  const soltar = (evento: DragEvent) => {
    evento.preventDefault();
    setArrastando(0);
    if (disabled) return;
    receber(evento.dataTransfer.files);
  };

  return (
    <div
      onDragOver={(evento) => {
        evento.preventDefault();
        if (!disabled) setArrastando(evento.dataTransfer.items.length || 1);
      }}
      onDragLeave={() => {
        setArrastando(0);
      }}
      onDrop={soltar}
      className={cx(
        // content-center: título e descrição ficam juntos mesmo se a área ganhar altura.
        'relative grid content-center justify-items-center rounded-painel border-2 border-dashed text-center transition-[background-color,border-color,transform,padding] duration-[var(--duracao-media)] ease-saida',
        compacto ? 'px-4 py-3' : 'px-6 py-10',
        arrastando > 0
          ? 'scale-101 border-marca bg-marca-suave'
          : 'border-borda-forte bg-superficie',
        disabled && 'opacity-60',
      )}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => entrada.current?.click()}
        className={cx(
          'rounded-cartao p-2',
          compacto ? 'flex items-center gap-3 text-left' : 'grid justify-items-center gap-3',
        )}
        aria-describedby={`${id}-descricao`}
      >
        <Rotulo
          compacto={compacto}
          arrastando={arrastando}
          titulo={titulo}
          descricao={descricao}
          idDescricao={`${id}-descricao`}
        />
      </button>
      <input
        ref={entrada}
        type="file"
        className="sr-only"
        tabIndex={-1}
        aria-label={titulo}
        multiple={multiplo}
        accept={aceita?.join(',')}
        onChange={(evento) => {
          receber(evento.target.files);
          evento.target.value = '';
        }}
      />
    </div>
  );
}
